import React, { FC, useContext, useMemo } from 'react';

import { EvComboboxOption, EvIcon, EvSection, EvSelectNew } from '@evinced-private/ui-common';

import { getInitialSelectedTimezone, parseTimeStr } from '../../../helpers/SchedulingHelper';
import { FrequencyDto } from '../../../interfaces/ScheduledJobConfiguration';
import BlueInfoIcon from '../../icons/BlueInfoIcon.svg?raw';
import { SchedulingSectionContext } from '../context';
import { getSelectedTime } from '../helpers';
import { MONTH_OPTIONS } from '../SchedulingSections.constants';

import { MONTH_SCHEDULING_LABELS } from './labels';
import {
	INPUT_LIST,
	LastDayOfMonthOption,
	MINIMUM_DAYS_TO_SHOW_WARNING
} from './MonthlyScheduling.constants';
import { INPUT_TYPE } from './MonthlyScheduling.types';

import './MonthlyScheduling.scss';

export const MonthScheduling: FC = () => {
	const { config, updateConfig, error } = useContext(SchedulingSectionContext);

	const monthOptionsWithYears = useMemo(() => {
		const now = new Date();
		const currentMonth = now.getMonth() + 1;
		const currentYear = now.getFullYear();

		// Reorder months starting from current month
		const reorderedMonths = [
			...MONTH_OPTIONS.slice(currentMonth - 1),
			...MONTH_OPTIONS.slice(0, currentMonth - 1)
		];

		return reorderedMonths.map((month) => {
			const monthValue = month.value as number;
			const year = monthValue < currentMonth ? currentYear + 1 : currentYear;
			return {
				...month,
				label: `${month.label} ${year}`
			};
		});
	}, []);

	const selectedDayOfMonth = useMemo(() => {
		const result: EvComboboxOption[] = [];

		if (config?.mdays && config.mdays.length > 0) {
			const regularDays = config.mdays
				.filter((day) => day !== -1)
				.map((day) => ({ value: day, label: `Day ${day}` }));
			result.push(...regularDays);
		}

		if (config?.lastDayOfMonth || (config?.mdays && config.mdays.includes(-1))) {
			result.push({
				value: LastDayOfMonthOption.value,
				label: MONTH_SCHEDULING_LABELS.lastDayOfMonth
			});
		}

		return result.length > 0 ? result : undefined;
	}, [config?.mdays, config?.lastDayOfMonth]);

	const selectedTime = useMemo(() => getSelectedTime(config), [config]);

	const selectedTimezone = useMemo(() => {
		// Always returns user's timezone if not configured
		return [getInitialSelectedTimezone(config?.timezone)];
	}, [config?.timezone]);

	const selectedStartMonth = useMemo(() => {
		if (config?.startMonth) {
			return config?.startMonth
				? [
						{
							value: config.startMonth,
							label:
								monthOptionsWithYears.find((opt) => opt.value === config.startMonth)?.label || ''
						}
					]
				: undefined;
		}
		return undefined;
	}, [config?.startMonth, monthOptionsWithYears]);

	const handleSelectChange = (selectedItem: EvComboboxOption[], type: string): void => {
		const typeHandlers: Record<string, (item: EvComboboxOption[]) => void> = {
			[INPUT_TYPE.SELECTED_DAY_OF_MONTH]: (item) => {
				// Filter out lastDayOfMonthOption and only keep numeric day values
				const days = item
					.filter((day) => day.value !== LastDayOfMonthOption.value)
					.map((day) => day.value as number);
				const hasLastDayOfMonth = item.some((day) => day.value === LastDayOfMonthOption.value);
				updateConfig({ mdays: days, lastDayOfMonth: hasLastDayOfMonth });
			},
			[INPUT_TYPE.SELECTED_TIME]: (item) => {
				const timeStr = item[0].value as string;
				const { hours, minutes } = parseTimeStr(timeStr);
				updateConfig({ hours: [hours], minutes: [minutes] });
			},
			[INPUT_TYPE.SELECTED_TIMEZONE]: (item) => {
				updateConfig({ timezone: item[0].value as string });
			},
			[INPUT_TYPE.SELECTED_START_MONTH]: (item) => {
				const monthValue = item[0].value as number;
				updateConfig({ months: [monthValue], startMonth: monthValue });
			}
		};

		const handler = typeHandlers[type];
		if (handler) {
			handler(selectedItem);
		}
	};

	const showMonthlySelectionWarning = useMemo(() => {
		return config?.mdays?.some((day) => day >= MINIMUM_DAYS_TO_SHOW_WARNING);
	}, [config?.mdays]);

	const isMonthlySchedulingError = useMemo(() => {
		return error && error.payload?.frequency === FrequencyDto.MONTHLY;
	}, [error]);

	const typeToSelectedValueMap = useMemo(() => {
		return {
			[INPUT_TYPE.SELECTED_DAY_OF_MONTH]: selectedDayOfMonth,
			[INPUT_TYPE.SELECTED_TIME]: selectedTime,
			[INPUT_TYPE.SELECTED_TIMEZONE]: selectedTimezone,
			[INPUT_TYPE.SELECTED_START_MONTH]: selectedStartMonth
		};
	}, [selectedDayOfMonth, selectedTime, selectedTimezone, selectedStartMonth]);

	return (
		<EvSection className="month-scheduling-section">
			<div className="month-scheduling-content">
				<fieldset>
					{INPUT_LIST.map((input) => {
						const { id, type, label, placeholder, options, isSearchable, isMultiple } = input;

						const selectedValue = typeToSelectedValueMap[type];

						// Use month options with years for start month selector
						const displayOptions =
							type === INPUT_TYPE.SELECTED_START_MONTH ? monthOptionsWithYears : options;

						return (
							<EvSelectNew
								key={id}
								id={id}
								options={displayOptions}
								selectedValue={selectedValue}
								onSelectChange={(selectedItem) => handleSelectChange(selectedItem, type)}
								placeholder={placeholder}
								isSearchable={isSearchable}
								label={label}
								isMultiple={isMultiple}
								className="month-scheduling-select"
								shouldSortOptions={false}
							/>
						);
					})}
				</fieldset>
				<div className="month-scheduling-error">{isMonthlySchedulingError && error.message}</div>
				{showMonthlySelectionWarning && (
					<div className="month-selection-warning">
						<EvIcon icon={BlueInfoIcon} />
						{MONTH_SCHEDULING_LABELS.monthlySelectionWarning}
					</div>
				)}
			</div>
		</EvSection>
	);
};
